import { StationInfoRequest } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/requestSchema";
import { FormSchema } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/formSchema";
import { getPreferredIdForStop, Stop, StopId } from "@vygruppen/vy-train-map";
import { StationEventResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/responseSchema";
import { DefaultValues } from "react-hook-form";

const mapStopToPreferredStopId = (stop: Stop): StopId => {
  const stopId = getPreferredIdForStop(stop);
  if (!stopId)
    throw new Error(`Stretch contains malformed stop ${JSON.stringify(stop)}`);

  return stopId;
};

const mapStopsToPreferredStopId = (stops: Stop[]): StopId[] =>
  stops.map(mapStopToPreferredStopId);

export function formToStationInformationRequest({
  stationForm,
}: FormSchema): StationInfoRequest {
  switch (stationForm.stationInformationType) {
    case "STATION_THREAT_LEVEL":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        threatLevel: stationForm.threatLevel,
      };

    case "STATION_SALES_CHANNEL_REDUCED":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        meansOfPayment: stationForm.meansOfPayment,
      };

    case "STATION_DIFFICULT_CUSTOMER":
    case "STATION_WANTED_PERSON":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        personDescription: stationForm.personDescription,
      };

    case "STATION_ELEVATOR_FAULT":
    case "STATION_SPEAKER_FAULT":
    case "STATION_TRACK_MONITOR_FAULT":
    case "STATION_WHEELCHAIR_RAMP_FAULT": {
      const sortedTracks = stationForm.affectedTracks.toSorted((a, b) => a - b);
      return {
        stationInformationType: stationForm.stationInformationType,
        stop: mapStopToPreferredStopId(stationForm.stop),
        affectedTracks: sortedTracks,
      };
    }

    case "STATION_ARRIVAL_AND_DEPARTURE_MONITOR_FAULT": {
      const sortedTracks = stationForm.affectedTracks?.toSorted(
        (a, b) => a - b,
      );
      return {
        stationInformationType: stationForm.stationInformationType,
        stop: mapStopToPreferredStopId(stationForm.stop),
        affectedTracks: sortedTracks ?? [],
      };
    }

    case "STATION_COMMUNICATION_SYSTEMS_DOWN":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        affectedSystems: stationForm.affectedSystems,
      };

    case "STATION_DROPS_OPERATION":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        level: stationForm.level,
      };

    case "STATION_GENERAL":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
        severity: stationForm.severity,
        distributions: stationForm.distributions,
      };

    case "STATION_PLATFORM_FAULT":
    case "STATION_ALL_SALES_CHANNELS_DOWN":
    case "STATION_APP_SALES_CHANNEL_DOWN":
    case "STATION_MOBILE_TERMINALS_SALES_CHANNEL_DOWN":
    case "STATION_TICKET_MACHINE_DOWN":
    case "STATION_TICKET_VALIDATOR_DOWN":
    case "STATION_DEPOSIT_MACHINE_DOWN":
    case "STATION_MAIN_MONITOR_FAULT":
    case "STATION_PLATFORM_SLIPPERY":
    case "STATION_PARKING_ISSUE":
    case "STATION_ENTRANCE_CHANGED":
    case "STATION_WAITING_ROOM_CLOSED":
    case "STATION_CLOSED_FOR_PASSENGERS":
    case "STATION_DROPS_EVACUATION":
      return {
        stationInformationType: stationForm.stationInformationType,
        stops: mapStopsToPreferredStopId(stationForm.stops),
      };
  }
}

export const stationEventResponseToForm = (
  event: StationEventResponse,
): DefaultValues<FormSchema["stationForm"]> => {
  switch (event.stationInformationType) {
    case "STATION_THREAT_LEVEL":
    case "STATION_SALES_CHANNEL_REDUCED":
    case "STATION_DIFFICULT_CUSTOMER":
    case "STATION_WANTED_PERSON":
    case "STATION_COMMUNICATION_SYSTEMS_DOWN":
    case "STATION_DROPS_OPERATION":
    case "STATION_GENERAL":
    case "STATION_PLATFORM_FAULT":
    case "STATION_ALL_SALES_CHANNELS_DOWN":
    case "STATION_APP_SALES_CHANNEL_DOWN":
    case "STATION_MOBILE_TERMINALS_SALES_CHANNEL_DOWN":
    case "STATION_TICKET_MACHINE_DOWN":
    case "STATION_TICKET_VALIDATOR_DOWN":
    case "STATION_DEPOSIT_MACHINE_DOWN":
    case "STATION_MAIN_MONITOR_FAULT":
    case "STATION_PLATFORM_SLIPPERY":
    case "STATION_PARKING_ISSUE":
    case "STATION_ENTRANCE_CHANGED":
    case "STATION_WAITING_ROOM_CLOSED":
    case "STATION_CLOSED_FOR_PASSENGERS":
    case "STATION_DROPS_EVACUATION": {
      const stops = event.stops.map<Stop>((stopId) => ({
        name: stopId.name ?? stopId.id,
        countryCode: event.countryCode,
        stopIds: [stopId],
      }));
      return { ...event, stops };
    }

    case "STATION_ARRIVAL_AND_DEPARTURE_MONITOR_FAULT":
    case "STATION_ELEVATOR_FAULT":
    case "STATION_SPEAKER_FAULT":
    case "STATION_TRACK_MONITOR_FAULT":
    case "STATION_WHEELCHAIR_RAMP_FAULT": {
      const stop = {
        name: event.stop.name ?? event.stop.id,
        countryCode: event.countryCode,
        stopIds: [event.stop],
      };
      return { ...event, stop };
    }
  }
};
